import { useState } from "react";
import { useAdminSession, useInvalidateAdminSession } from "@/hooks/use-admin-session";
import { Button } from "@/components/ui/button";

// Private admin hub. Unlinked from public navigation (the navbar shows an
// Admin link only once a server-side session exists) and noindex. The
// credential check itself happens server-side — this page never compares
// anything locally.

const TOOLS: Array<{ href: string; title: string; desc: string; external?: boolean }> = [
  {
    href: "/api/dashboard",
    title: "Live funnel dashboard",
    desc: "Real-time funnel progression, per-source breakdown, and the sale chime.",
    external: true,
  },
  {
    href: "/funnels",
    title: "Marketing funnels",
    desc: "Funnel landing pages and previews (unlisted).",
  },
  {
    href: "/peptides",
    title: "Peptide intel",
    desc: "Internal peptide market dashboard.",
  },
  {
    href: "/launchos/",
    title: "Peptide LaunchOS",
    desc: "Launch operations app.",
    external: true,
  },
];

export default function Admin() {
  const { authed } = useAdminSession();
  const invalidate = useInvalidateAdminSession();
  const [username, setUsername] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function login(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/dashboard/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password: pw }),
      });
      if (res.ok) {
        setUsername("");
        setPw("");
        invalidate();
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? "Wrong password.");
      }
    } catch {
      setError("Network error — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    await fetch("/api/dashboard/logout", { method: "POST" });
    invalidate();
  }

  return (
    <div className="min-h-screen pt-32 pb-24">
      <title>Admin — Travis Johnson</title>
      <meta name="robots" content="noindex, nofollow" />
      <div className="container mx-auto px-6 max-w-2xl">
        <h1 className="text-3xl font-serif font-semibold tracking-tight mb-2">Admin</h1>

        {!authed ? (
          <form onSubmit={login} className="mt-8 max-w-sm space-y-4" data-testid="form-admin-login">
            <p className="text-muted-foreground text-sm">This area is private.</p>
            <input
              type="text"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              autoComplete="username"
              autoFocus
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              data-testid="input-admin-username"
            />
            <input
              type="password"
              name="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              data-testid="input-admin-password"
            />
            <Button type="submit" disabled={busy || username.length === 0 || pw.length === 0} data-testid="button-admin-login">
              {busy ? "Checking…" : "Enter"}
            </Button>
            {error && (
              <p className="text-sm text-destructive" data-testid="text-admin-error">
                {error}
              </p>
            )}
          </form>
        ) : (
          <>
            <div className="flex items-center justify-between mt-1 mb-8">
              <p className="text-muted-foreground text-sm">Your tools. This page is unlisted.</p>
              <button
                onClick={logout}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-admin-logout"
              >
                Log out
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {TOOLS.map((t) => (
                <a
                  key={t.href}
                  href={t.href}
                  className="group rounded-lg border border-border p-5 hover:border-foreground/30 hover:bg-foreground/[0.03] transition-colors"
                  data-testid={`link-admin-tool-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="font-medium group-hover:underline underline-offset-4">
                    {t.title}
                    {t.external && <span className="text-muted-foreground ml-1">↗</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
